import type { FC } from 'react';
import React, { memo, useEffect, useRef } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { LazyHomepageCard } from '@confluence/team-calendar';
import { ExperienceSuccess } from '@confluence/experience-tracker/entry-points/component.ts';
import { HOME_RIGHT_PANEL_CALENDARS_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';

import { CollapsibleSection } from '../CollapsibleSection.tsx';
import { useHomeWidgetState } from '../useHomeWidgetState.ts';
import { HomeWidgetState } from '../__types__/HomeWidgetsQuery.ts';

type CalendarsProps = {
	completeKey: string;
	noCollapse?: boolean;
	inRightPanel?: boolean;
};

const CALENDARS_SECTION_ID =
	'com.atlassian.confluence.extra.team-calendars:newDashboadUpcomingEvents';

const i18n = defineMessages({
	heading: {
		id: 'home.calendar.title',
		defaultMessage: 'Calendars',
		description: "Title for home's calendar",
	},
});

export const Calendars: FC<CalendarsProps> = memo(
	({ completeKey, noCollapse = false, inRightPanel = false }) => {
		const header = <FormattedMessage {...i18n.heading} />;

		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { widgetState } = useHomeWidgetState(completeKey);
		const isCollapsed = widgetState === HomeWidgetState.COLLAPSED;
		const firedRenderedEventRef = useRef(false);

		useEffect(() => {
			if (isCollapsed) {
				firedRenderedEventRef.current = false;
			} else if (!firedRenderedEventRef.current && inRightPanel) {
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'rendered',
						actionSubject: 'collapsibleSectionContent',
						actionSubjectId: CALENDARS_SECTION_ID,
						source: 'rightPanel',
						attributes: {
							homeVersion: '1.5',
						},
					},
				}).fire();
				firedRenderedEventRef.current = true;
			}
		}, [isCollapsed, inRightPanel, createAnalyticsEvent]);

		if (inRightPanel) {
			if (noCollapse) {
				return (
					<>
						<LazyHomepageCard />
						<ExperienceSuccess name={HOME_RIGHT_PANEL_CALENDARS_EXPERIENCE} />
					</>
				);
			}

			return (
				<CollapsibleSection sectionId={completeKey} header={header} key={completeKey}>
					<LazyHomepageCard />
					<ExperienceSuccess name={HOME_RIGHT_PANEL_CALENDARS_EXPERIENCE} />
				</CollapsibleSection>
			);
		}

		return <LazyHomepageCard />;
	},
);
